import React, { useEffect, useState } from "react";
import AdminService from "../../services/AdminService";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core";
import MaterialTable from "material-table";
import TableIcons from "../molecules/TableIcons";
import moment from "moment";
import { PriceDisplay } from "../atoms/PriceDisplay";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const pct = (value, total) => {
    if (!total || total <= 0) return 0;
    return +((+value * 100) / +total).toFixed(0) || 0;
};

const getExtra = (budget) => Math.max(0, +budget.total - +budget.amountForRenewal);

const AddExtraCreditsDialog = ({ open, onClose, currentBudget, companyName, onConfirm, loading }) => {
    const [amount, setAmount] = useState('');

    useEffect(() => {
        if (open) setAmount('');
    }, [open]);

    if (!currentBudget) return null;

    const contract = +currentBudget.amountForRenewal || 0;
    const extraNow = getExtra(currentBudget);
    const totalNow = +currentBudget.total || 0;
    const toAdd = +amount || 0;
    const extraAfter = extraNow + toAdd;
    const totalAfter = totalNow + toAdd;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Agregar créditos extra</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    Los créditos extra se suman al total disponible <b>sin modificar</b> el contrato mensual.
                </Typography>
                <Box style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                    <Typography variant="body2">Contrato mensual: <b>{contract}</b> (sin cambios)</Typography>
                    <Typography variant="body2">Créditos extra actuales: <b>{extraNow}</b></Typography>
                    <Typography variant="body2">Total disponible actual: <b>{totalNow}</b></Typography>
                </Box>
                <TextField
                    label="Créditos extra a agregar"
                    type="number"
                    fullWidth
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputProps={{ min: 1 }}
                    autoFocus
                />
                {toAdd > 0 && (
                    <Box style={{ marginTop: 16, padding: 12, background: '#e8f5e9', borderRadius: 4 }}>
                        <Typography variant="body2">
                            Después: Contrato <b>{contract}</b> → Extra <b>{extraAfter}</b> → Total <b>{totalAfter}</b>
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancelar</Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={loading || !toAdd || toAdd <= 0}
                    onClick={() => onConfirm(toAdd)}
                >
                    Agregar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const PointsBudgetsListModal = ({ company, open, setOpen }) => {
    const [pointsBudgets, setPointsBudgets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [extraDialogOpen, setExtraDialogOpen] = useState(false);

    const currentBudget = pointsBudgets[0] || null;

    const getPointsBudgets = async () => {
        try {
            setLoading(true);
            const res = await AdminService.getCompanyPointsBudgets(company.id);
            const budgets = (res?.data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
            setPointsBudgets(budgets);
            res && setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    };

    const updateBudget = async (points, mode, durationInMonths, soldValueInUsd) => {
        try {
            setLoading(true);
            await AdminService.updatePointsBudget(company?.id, points, mode, durationInMonths, soldValueInUsd);
        } catch (error) {
            console.log(error);
        } finally {
            getPointsBudgets();
        }
    };

    const handleAddExtra = async (pointsToAdd) => {
        await updateBudget(pointsToAdd, 'ADD', currentBudget?.durationInMonths, currentBudget?.soldValueInUsd);
        setExtraDialogOpen(false);
    };

    const handleRowUpdate = async (newData) => {
        const oldData = pointsBudgets[0];
        if (!oldData) return Promise.reject();

        const contractChanged = +newData.amountForRenewal !== +oldData.amountForRenewal;

        if (contractChanged) {
            const confirmed = window.confirm(
                `Esto cambia el contrato mensual de ${oldData.amountForRenewal} a ${newData.amountForRenewal}.\n\n` +
                'No agrega créditos extra. Los créditos extra existentes se conservan.\n\n¿Continuar?'
            );
            if (!confirmed) return Promise.reject();
            await updateBudget(+newData.amountForRenewal, 'OVERRIDE', +newData.durationInMonths, +newData.soldValueInUsd);
        } else {
            await updateBudget(+oldData.amountForRenewal, 'UPDATE_META', +newData.durationInMonths, +newData.soldValueInUsd);
        }
    };

    const markAsPaid = (pointsBudgetId) => {
        setLoading(true);
        AdminService.setPointsBudgetPaid(company?.id, pointsBudgetId, true).then(async () => {
            await getPointsBudgets();
            setLoading(false);
        }).catch(console.log);
    };

    useEffect(() => {
        open && getPointsBudgets();
    }, [company, open]);

    const renderUsedCell = (b) => {
        const used = +b.usedAmount || 0;
        const total = +b.total || 0;
        const contract = +b.amountForRenewal || 0;
        const pctTotal = pct(used, total);
        const pctContract = pct(used, contract);
        const overContract = contract > 0 && used > contract;

        return (
            <div>
                <div>{used} ({pctTotal}% del total)</div>
                <LinearProgress variant="determinate" value={Math.min(pctTotal, 100)} />
                {contract > 0 && (
                    <Typography
                        variant="caption"
                        style={{ color: overContract ? '#e65100' : '#757575', fontWeight: overContract ? 600 : 400 }}
                    >
                        {pctContract}% del contrato
                    </Typography>
                )}
            </div>
        );
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth={true} maxWidth={'lg'}>
            <DialogTitle>
                <b>{''}</b>
            </DialogTitle>
            <DialogContent>
                {loading && !pointsBudgets.length ?
                    <CircularProgress style={{ marginLeft: 600 }} />
                    :
                    <>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                            El <b>contrato mensual</b> es el presupuesto base. Los <b>créditos extra</b> se suman al total disponible sin modificar el contrato.
                        </Typography>

                        {currentBudget && (
                            <Box style={{
                                display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16,
                                padding: 12, background: '#fafafa', borderRadius: 4, border: '1px solid #eee',
                            }}>
                                <Typography variant="body2">Mes actual: <b>{moment(currentBudget.date).utc().format('MMMM YYYY')}</b></Typography>
                                <Typography variant="body2">Contrato: <b>{currentBudget.amountForRenewal}</b></Typography>
                                <Typography variant="body2">Extra: <b>{getExtra(currentBudget) > 0 ? `+${getExtra(currentBudget)}` : 0}</b></Typography>
                                <Typography variant="body2">Total: <b>{currentBudget.total}</b></Typography>
                                <Typography variant="body2">Usado: <b>{currentBudget.usedAmount}</b></Typography>
                                <Typography variant="body2">Disponible: <b>{currentBudget.availableAmount}</b></Typography>
                            </Box>
                        )}

                        <MaterialTable
                            icons={TableIcons}
                            title={
                                <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>{'Presupuestos de '}<b>{company?.name}</b></Box>
                                </Box>
                            }
                            editable={{
                                onRowUpdate: handleRowUpdate,
                                isEditable: rowData => rowData?.tableData?.id === 0,
                            }}
                            options={{ pageSize: 10, pageSizeOptions: [10, 25, 50, 100], sorting: true, thirdSortClick: false }}
                            columns={[
                                { title: 'ID', render: b => <div>{`#${b.id}`}</div> },
                                { title: 'Mes', render: b => <div>{moment(b.date).utc().format('MMMM YYYY')}</div> },
                                {
                                    title: (
                                        <Tooltip title="Presupuesto base mensual. Para agregar créditos puntuales usar 'Agregar créditos extra'.">
                                            <span>Contrato mensual <InfoOutlinedIcon style={{ fontSize: 14, verticalAlign: 'middle' }} /></span>
                                        </Tooltip>
                                    ),
                                    render: b => <div>{b.amountForRenewal}</div>,
                                    field: 'amountForRenewal',
                                },
                                {
                                    title: 'Créditos extra',
                                    render: b => {
                                        const extra = getExtra(b);
                                        return extra > 0
                                            ? <Chip label={`+${extra}`} size="small" style={{ background: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                                            : <div>0</div>;
                                    },
                                },
                                { title: 'Usado', render: renderUsedCell },
                                { title: 'Disponible', render: b => <div>{b.availableAmount}</div> },
                                { title: 'Total disponible', render: b => <div><b>{b.total}</b></div> },
                                { title: 'Renovacion cada', render: b => <div>{b.durationInMonths} {b.durationInMonths > 1 ? 'meses' : 'mes'}</div>, field: 'durationInMonths' },
                                { title: 'Valor de Venta del Point', render: b => <div><PriceDisplay value={b.soldValueInUsd} currency="USD" /></div>, field: 'soldValueInUsd' },
                                {
                                    title: <b>Pagado</b>,
                                    render: rowData => rowData.paid
                                        ? <CheckCircleIcon style={{ paddingLeft: "30px", color: "green", alignContent: "center" }} />
                                        : '',
                                },
                                {
                                    title: <b>Acciones</b>,
                                    render: rowData => !rowData?.paid && (
                                        <Tooltip title="Marcar como pagado">
                                            <IconButton color="primary" onClick={() => markAsPaid(rowData.id)} disabled={loading}>
                                                <ThumbUpIcon />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                },
                            ]}
                            actions={[
                                {
                                    icon: TableIcons.Add,
                                    tooltip: 'Agregar créditos extra (no modifica el contrato mensual)',
                                    isFreeAction: true,
                                    onClick: () => setExtraDialogOpen(true),
                                },
                            ]}
                            data={pointsBudgets}
                            localization={{
                                pagination: { labelDisplayedRows: '{from}-{to} de {count}' },
                                header: { actions: '' },
                                body: {
                                    emptyDataSourceMessage: 'No hay presupuestos para mostrar',
                                    filterRow: { filterTooltip: 'Filtros' },
                                },
                            }}
                        />

                        <AddExtraCreditsDialog
                            open={extraDialogOpen}
                            onClose={() => setExtraDialogOpen(false)}
                            currentBudget={currentBudget}
                            companyName={company?.name}
                            onConfirm={handleAddExtra}
                            loading={loading}
                        />
                    </>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)} color="primary">Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};
